import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';
import { BankTransaction, BankTransactionCreate, BankTransactionUpdate } from '../models/BankTransaction';

export interface BankTransactionPagedResult {
  items: BankTransaction[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class BankTransactionsService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('banktransactions', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  getByStatement(statementId: number, options?: PaginationOptions): Observable<BankTransactionPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/statement/${statementId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações por extrato');
        return throwError(() => err);
      })
    );
  }

  getUnlinked(options?: PaginationOptions): Observable<BankTransactionPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/unlinked'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações não conciliadas');
        return throwError(() => err);
      })
    );
  }

  getByDateRange(start: string, end: string, options?: PaginationOptions): Observable<BankTransactionPagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/date-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações por período');
        return throwError(() => err);
      })
    );
  }

  getAll(): Observable<BankTransaction[]> {
    return this.http.get<BankTransaction[]>(`${this.financialUrl()}`).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações bancárias');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<BankTransaction> {
    return this.http.get<BankTransaction>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transação bancária');
        return throwError(() => err);
      })
    );
  }

  create(payload: BankTransactionCreate): Observable<BankTransaction> {
    return this.http.post<BankTransaction>(`${this.financialUrl()}`, payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar transação bancária');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: BankTransactionUpdate): Observable<BankTransaction> {
    return this.http.put<BankTransaction>(this.financialUrl(`/${id}`), payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar transação bancária');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao deletar transação bancária');
        return throwError(() => err);
      })
    );
  }

  private mapPagedResponse(response: any): BankTransactionPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems;

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
